/**
 * Batches module
 * Handles batch job tracking and management in the dashboard
 */

import { API } from '../core/api.js';
import { showNotification } from '../components/notifications.js';

let batchPollingInterval = null;
let podcastNameCache = {};
let isInitialLoad = true;

/**
 * Load and render active batches
 */
export async function loadBatches() {
    const container = document.getElementById('active-batches');
    if (!container) return;

    try {
        const data = await API.get('/api/batches?limit=10');
        const batches = data.batches || [];

        // Filter to show only active (pending/running) batches
        const activeBatches = batches.filter(b =>
            b.status === 'pending' || b.status === 'running'
        );

        if (activeBatches.length === 0) {
            if (container.querySelector('.batch-card') || isInitialLoad) {
                container.textContent = '';
                const emptyDiv = document.createElement('div');
                emptyDiv.className = 'batch-empty';
                emptyDiv.textContent = 'No active batch jobs';
                container.appendChild(emptyDiv);
            }
            stopBatchPolling();
            isInitialLoad = false;
            return;
        }

        // Fetch podcast names for new podcasts only
        await cachePodcastNames(activeBatches);

        // Get current batch IDs in DOM
        const existingCards = container.querySelectorAll('.batch-card');
        const existingIds = new Set();
        existingCards.forEach(card => existingIds.add(card.dataset.batchId));

        // Get new batch IDs
        const newIds = new Set(activeBatches.map(b => b.batch_id));

        // Remove cards for batches that are no longer active
        existingCards.forEach(card => {
            if (!newIds.has(card.dataset.batchId)) {
                card.remove();
            }
        });

        // Remove empty message if present
        const emptyMsg = container.querySelector('.batch-empty');
        if (emptyMsg) emptyMsg.remove();

        // Update or create cards
        for (const batch of activeBatches) {
            const existingCard = container.querySelector(`[data-batch-id="${batch.batch_id}"]`);
            if (existingCard) {
                // Update in place
                updateBatchCard(existingCard, batch);
            } else {
                // Create new card
                const card = createBatchCard(batch, podcastNameCache[batch.podcast_id]);
                container.appendChild(card);
            }
        }

        // Start polling if we have active batches
        startBatchPolling();
        isInitialLoad = false;

    } catch (error) {
        console.error('Error loading batches:', error);
        if (isInitialLoad) {
            container.textContent = '';
            const errorDiv = document.createElement('div');
            errorDiv.className = 'batch-error';
            errorDiv.textContent = 'Failed to load batches';
            container.appendChild(errorDiv);
        }
    }
}

/**
 * Cache podcast names (only fetches uncached ones)
 */
async function cachePodcastNames(batches) {
    const uncachedIds = batches
        .map(b => b.podcast_id)
        .filter(id => !podcastNameCache[id]);

    const uniqueIds = [...new Set(uncachedIds)];

    for (const id of uniqueIds) {
        try {
            const podcast = await API.get(`/api/podcasts/${id}`);
            podcastNameCache[id] = podcast.title || `Podcast ${id}`;
        } catch {
            podcastNameCache[id] = `Podcast ${id}`;
        }
    }
}

/**
 * Update an existing batch card in place (no flicker)
 */
function updateBatchCard(card, batch) {
    const progress = batch.total > 0
        ? Math.round((batch.completed / batch.total) * 100)
        : 0;

    // Update progress bar
    const progressFill = card.querySelector('.batch-progress-fill');
    if (progressFill) {
        progressFill.style.width = `${progress}%`;
    }

    // Update progress text
    const progressText = card.querySelector('.batch-progress-text');
    if (progressText) {
        let textContent = `${batch.completed}/${batch.total}`;
        if (batch.failed > 0) {
            textContent += ` (${batch.failed} failed)`;
        }
        if (batch.status === 'pending') {
            textContent += ' waiting...';
        }
        progressText.textContent = textContent;
    }

    // Update status class
    const isRunning = batch.status === 'running';
    card.classList.toggle('batch-running', isRunning);
    card.classList.toggle('batch-pending', !isRunning);
}

/**
 * Create a batch card DOM element
 */
function createBatchCard(batch, podcastName) {
    const progress = batch.total > 0
        ? Math.round((batch.completed / batch.total) * 100)
        : 0;

    const typeIcons = {
        'ai_analysis': '🧠',
        'transcription': '📝',
        'diarization': '🎙️'
    };

    const typeLabels = {
        'ai_analysis': 'AI Analysis',
        'transcription': 'Transcription',
        'diarization': 'Diarization'
    };

    const icon = typeIcons[batch.type] || '📦';
    const label = typeLabels[batch.type] || batch.type;
    const statusClass = batch.status === 'running' ? 'batch-running' : 'batch-pending';

    // Create card container
    const card = document.createElement('div');
    card.className = `batch-card ${statusClass}`;
    card.dataset.batchId = batch.batch_id;

    // Header
    const header = document.createElement('div');
    header.className = 'batch-header';

    const typeSpan = document.createElement('span');
    typeSpan.className = 'batch-type';
    typeSpan.textContent = `${icon} ${label}`;

    const podcastSpan = document.createElement('span');
    podcastSpan.className = 'batch-podcast';
    podcastSpan.textContent = podcastName || `Podcast ${batch.podcast_id}`;

    header.appendChild(typeSpan);
    header.appendChild(podcastSpan);

    // Progress section
    const progressDiv = document.createElement('div');
    progressDiv.className = 'batch-progress';

    const progressBar = document.createElement('div');
    progressBar.className = 'batch-progress-bar';

    const progressFill = document.createElement('div');
    progressFill.className = 'batch-progress-fill';
    progressFill.style.width = `${progress}%`;
    progressBar.appendChild(progressFill);

    const progressText = document.createElement('div');
    progressText.className = 'batch-progress-text';

    let textContent = `${batch.completed}/${batch.total}`;
    if (batch.failed > 0) {
        textContent += ` (${batch.failed} failed)`;
    }
    if (batch.status === 'pending') {
        textContent += ' waiting...';
    }
    progressText.textContent = textContent;

    progressDiv.appendChild(progressBar);
    progressDiv.appendChild(progressText);

    // Actions
    const actions = document.createElement('div');
    actions.className = 'batch-actions';

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn btn-small btn-secondary';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => cancelBatch(batch.batch_id));

    actions.appendChild(cancelBtn);

    // Assemble card
    card.appendChild(header);
    card.appendChild(progressDiv);
    card.appendChild(actions);

    return card;
}

/**
 * Cancel a batch job
 */
export async function cancelBatch(batchId) {
    try {
        await API.delete(`/api/batches/${batchId}`);
        showNotification('Batch cancelled', 'success');
        // Remove the card immediately
        const card = document.querySelector(`[data-batch-id="${batchId}"]`);
        if (card) card.remove();
        // Check if container is empty
        const container = document.getElementById('active-batches');
        if (container && !container.querySelector('.batch-card')) {
            container.textContent = '';
            const emptyDiv = document.createElement('div');
            emptyDiv.className = 'batch-empty';
            emptyDiv.textContent = 'No active batch jobs';
            container.appendChild(emptyDiv);
        }
    } catch (error) {
        console.error('Error cancelling batch:', error);
        showNotification('Failed to cancel batch', 'error');
    }
}

/**
 * Start polling for batch updates
 */
export function startBatchPolling() {
    if (batchPollingInterval) return;

    batchPollingInterval = setInterval(() => {
        loadBatches();
    }, 5000);
}

/**
 * Stop polling for batch updates
 */
export function stopBatchPolling() {
    if (batchPollingInterval) {
        clearInterval(batchPollingInterval);
        batchPollingInterval = null;
    }
}

// Expose cancelBatch globally for onclick handlers
window.cancelBatch = cancelBatch;
